const WHITE = '#FFFFFF'
const DARK_FOREGROUND = '#111827'

export const FIXED_TABLE_PAGE_BACKGROUND = WHITE

function parseThemeColorChannels(color) {
  const value = typeof color === 'string' ? color.trim().toUpperCase() : ''
  if (/^#[0-9A-F]{6}$/.test(value)) {
    return [1, 3, 5].map((index) => Number.parseInt(value.slice(index, index + 2), 16))
  }
  if (/^#[0-9A-F]{3}$/.test(value)) {
    return value.slice(1).split('').map((part) => Number.parseInt(`${part}${part}`, 16))
  }

  const rgb = value.match(/^RGB\(\s*(-?\d+(?:\.\d+)?%?)\s*,\s*(-?\d+(?:\.\d+)?%?)\s*,\s*(-?\d+(?:\.\d+)?%?)\s*\)$/)
  if (!rgb) return null

  const channels = rgb.slice(1).map((channel) => {
    const parsed = Number.parseFloat(channel)
    const isPercentage = channel.endsWith('%')
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > (isPercentage ? 100 : 255)) return null
    return isPercentage ? parsed * 2.55 : parsed
  })
  return channels.every((channel) => channel !== null) ? channels : null
}

export function isValidThemeColor(color) {
  return Boolean(parseThemeColorChannels(color))
}

function normalizeHex(color, fallback = '#7D947D') {
  const channels = parseThemeColorChannels(color)
  return channels ? channelsToHex(channels) : fallback
}

function hexChannels(color) {
  const value = normalizeHex(color)
  return [1, 3, 5].map((index) => Number.parseInt(value.slice(index, index + 2), 16))
}

function channelsToHex(channels) {
  return `#${channels.map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}

export function mixHex(color, target, amount) {
  const ratio = Math.max(0, Math.min(1, Number(amount) || 0))
  const sourceChannels = hexChannels(color)
  const targetChannels = hexChannels(target)
  return channelsToHex(sourceChannels.map((channel, index) => (
    channel + (targetChannels[index] - channel) * ratio
  )))
}

function relativeLuminance(color) {
  return hexChannels(color)
    .map((channel) => {
      const value = channel / 255
      return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
    })
    .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index], 0)
}

export function contrastRatio(first, second) {
  const firstLuminance = relativeLuminance(first)
  const secondLuminance = relativeLuminance(second)
  const high = Math.max(firstLuminance, secondLuminance)
  const low = Math.min(firstLuminance, secondLuminance)
  return (high + 0.05) / (low + 0.05)
}

export function ensureReadableText(preferred, background, minimumRatio = 4.5) {
  const preferredColor = normalizeHex(preferred, DARK_FOREGROUND)
  if (contrastRatio(preferredColor, background) >= minimumRatio) return preferredColor

  for (let step = 1; step <= 20; step += 1) {
    const amount = step / 20
    const darkened = mixHex(preferredColor, '#000000', amount)
    if (contrastRatio(darkened, background) >= minimumRatio) return darkened
    const lightened = mixHex(preferredColor, WHITE, amount)
    if (contrastRatio(lightened, background) >= minimumRatio) return lightened
  }

  return contrastRatio(DARK_FOREGROUND, background) >= contrastRatio(WHITE, background)
    ? DARK_FOREGROUND
    : WHITE
}

/**
 * 为表格双行内容派生主、次文字色，不修改用户保存的正文颜色。
 * 正常配置保留主色；次色从混入25%背景色起逐步收紧至满足4.5对比度。
 * 主色缺乏淡化空间时自动增强主色，再计算可读且不同的次色。
 */
export function resolveTableTextColors(preferred, background) {
  const surface = normalizeHex(background, WHITE)
  let primary = ensureReadableText(preferred, surface)
  const secondaryFor = (color) => {
    for (let step = 25; step >= 1; step -= 1) {
      const candidate = mixHex(color, surface, step / 100)
      if (candidate !== color && contrastRatio(candidate, surface) >= 4.5) return candidate
    }
    return null
  }
  let secondary = secondaryFor(primary)
  if (!secondary) {
    primary = contrastRatio('#000000', surface) >= contrastRatio(WHITE, surface) ? '#000000' : WHITE
    secondary = secondaryFor(primary)
  }
  return { primary, secondary: secondary || primary }
}

export function readableControlForeground(background) {
  return contrastRatio(DARK_FOREGROUND, background) >= contrastRatio(WHITE, background)
    ? DARK_FOREGROUND
    : WHITE
}

function primaryShadow(primary) {
  const [red, green, blue] = hexChannels(primary)
  return `rgba(${red}, ${green}, ${blue}, 0.08)`
}

function createPalette(values) {
  const [
    primary,
    primaryDark,
    primaryLight,
    sideBackground,
    sideText,
    sideHover,
    headerBackground,
    headerText,
    headerBorder,
  ] = values.map((value) => normalizeHex(value))

  return Object.freeze({
    primary,
    primaryDark,
    primaryLight,
    sideBackground,
    sideText,
    sideHover,
    headerBackground,
    headerText,
    headerBorder,
    shadowColor: primaryShadow(primary),
  })
}

function freezeThemeConfig(config) {
  return Object.freeze({
    ...config,
    tableTheme: Object.freeze({ ...config.tableTheme }),
  })
}

function createThemeConfig(palette) {
  const headerTextColor = ensureReadableText(palette.headerText, palette.headerBackground)
  const bodyTextColor = ensureReadableText(palette.headerText, WHITE)
  return freezeThemeConfig({
    themeColor: palette.primary,
    buttonColor: palette.primary,
    useIndependentButtonColor: false,
    formLabelColor: bodyTextColor,
    tableTheme: {
      pageBackground: FIXED_TABLE_PAGE_BACKGROUND,
      surfaceBackground: WHITE,
      headerBackground: palette.headerBackground,
      headerTextColor,
      bodyBackground: WHITE,
      bodyTextColor,
      borderColor: palette.headerBorder,
      rowHoverBackground: palette.primaryLight,
      rowStripeBackground: mixHex(palette.headerBackground, WHITE, 0.68),
      scrollbarTrackColor: palette.headerBackground,
      scrollbarThumbColor: palette.primary,
      scrollbarThumbHoverColor: palette.primaryDark,
    },
  })
}

const PALETTE_DEFINITIONS = [
  ['sage', 'themeSage', 'themeSageDescription', ['#7D947D', '#5F7A5F', '#E8EEEA', '#3A4A3A', '#C2CFC2', '#4A5E4A', '#E6ECE6', '#546B54', '#D5E0D5']],
  ['mustard', 'themeMustard', 'themeMustardDescription', ['#A89860', '#86794A', '#F2ECD9', '#4C4631', '#D3C9AB', '#5E563D', '#EFE9D8', '#7D7148', '#E2DAC2']],
  ['taupe', 'themeTaupe', 'themeTaupeDescription', ['#9C8571', '#7A6756', '#EFE8E2', '#4A3F36', '#CDBFB4', '#5D4F44', '#EDE5DE', '#6F5C4C', '#DED2C8']],
  ['lavender', 'themeLavender', 'themeLavenderDescription', ['#8B7F9E', '#6C6180', '#ECE9F2', '#403A4C', '#C8C2D4', '#524A61', '#EAE7F0', '#5F5674', '#D8D3E3']],
  ['dustyblue', 'themeDustyBlue', 'themeDustyBlueDescription', ['#7D94A8', '#5F7690', '#E6ECF1', '#37434F', '#BCC8D1', '#475764', '#E5EBF0', '#4F6377', '#D3DDE5']],
  ['rose', 'themeRose', 'themeRoseDescription', ['#B58A8A', '#8F6B6B', '#F3EAEA', '#4A3A3A', '#CDBCBC', '#5D4A4A', '#F0E7E7', '#7A5C5C', '#E0D2D2']],
  ['pine', 'themePine', 'themePineDescription', ['#4E6B5B', '#38503F', '#E7ECE9', '#26372D', '#AEBFB4', '#33473B', '#E3EAE5', '#3D5647', '#D2DDD6']],
]

export const THEME_PRESETS = Object.freeze(PALETTE_DEFINITIONS.map((definition) => {
  const [id, nameKey, descriptionKey, values] = definition
  const palette = createPalette(values)
  return Object.freeze({
    id,
    nameKey,
    descriptionKey,
    palette,
    themeConfig: createThemeConfig(palette),
  })
}))

function deriveThemePalette(themeColor) {
  const primary = normalizeHex(themeColor)
  const primaryDark = mixHex(primary, '#000000', 0.24)
  const primaryLight = mixHex(primary, WHITE, 0.84)
  const sideBackground = mixHex(primary, '#111827', 0.64)
  const headerBackground = mixHex(primary, WHITE, 0.88)
  return {
    primary,
    primaryDark,
    primaryLight,
    sideBackground,
    sideText: ensureReadableText('#E5E7EB', sideBackground),
    sideHover: mixHex(sideBackground, WHITE, 0.1),
    headerBackground,
    headerText: ensureReadableText(primaryDark, headerBackground),
    headerBorder: mixHex(primaryLight, primary, 0.14),
    shadowColor: primaryShadow(primary),
  }
}

export function resolveThemePalette(themeColor) {
  const normalizedThemeColor = normalizeHex(themeColor)
  const preset = THEME_PRESETS.find((item) => item.palette.primary === normalizedThemeColor)
  return preset?.palette || deriveThemePalette(normalizedThemeColor)
}

const BUSINESS_BUTTON_PRIMARY_ANCHORS = Object.freeze({
  sage: '#68796B',
  mustard: '#7F7348',
  taupe: '#7A6756',
  lavender: '#6C6180',
  dustyblue: '#5F7690',
  rose: '#8F6B6B',
  pine: '#4E6B5B',
})

const SAGE_BUSINESS_BUTTON_BASE = Object.freeze({
  secondary: Object.freeze(['#F7F6F2', '#58645B', '#C7CEC6']),
  success: Object.freeze(['#E1E8DF', '#4D6251', '#C3CEC1']),
  warning: Object.freeze(['#ECE3D4', '#705D42', '#D5C7AE']),
  danger: Object.freeze(['#EADDDD', '#795455', '#D4BFBF']),
  info: Object.freeze(['#E2E7EA', '#4C6270', '#C5CFD5']),
  disabled: Object.freeze(['#F1F2EF', '#929A93', '#DFE3DE']),
})

function rgbaColor(color, alpha) {
  const [red, green, blue] = hexChannels(color)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

function ensureWhiteTextBackground(color) {
  const normalized = normalizeHex(color)
  if (contrastRatio(WHITE, normalized) >= 4.5) return normalized

  for (let step = 1; step <= 20; step += 1) {
    const darkened = mixHex(normalized, '#000000', step / 20)
    if (contrastRatio(WHITE, darkened) >= 4.5) return darkened
  }

  return '#000000'
}

function createBusinessButtonRole(values, options = {}) {
  const [backgroundValue, textValue, borderValue] = values
  const background = normalizeHex(backgroundValue)
  const border = normalizeHex(borderValue)
  const hoverBackground = options.disabled ? background : mixHex(background, '#000000', 0.04)
  const activeBackground = options.disabled ? background : mixHex(background, '#000000', 0.08)
  const text = options.disabled
    ? normalizeHex(textValue)
    : ensureReadableText(textValue, background)
  const role = {
    background,
    text,
    border,
    hoverBackground,
    hoverText: options.disabled ? text : ensureReadableText(text, hoverBackground),
    hoverBorder: options.disabled ? border : mixHex(border, '#000000', 0.06),
    activeBackground,
    activeText: options.disabled ? text : ensureReadableText(text, activeBackground),
    activeBorder: options.disabled ? border : mixHex(border, '#000000', 0.1),
  }

  if (options.primary) {
    role.boxShadow = `0 2px 6px ${rgbaColor(background, 0.14)}`
    role.activeBoxShadow = `0 1px 2px ${rgbaColor(background, 0.12)}`
  }

  return Object.freeze(role)
}

function resolveBusinessPrimaryBackground(buttonColor) {
  const normalizedButtonColor = normalizeHex(buttonColor)
  const preset = THEME_PRESETS.find((item) => item.palette.primary === normalizedButtonColor)
  return preset
    ? BUSINESS_BUTTON_PRIMARY_ANCHORS[preset.id]
    : ensureWhiteTextBackground(normalizedButtonColor)
}

function resolveThemeBusinessButtonValues(themeColor) {
  const normalizedThemeColor = normalizeHex(themeColor)
  const preset = THEME_PRESETS.find((item) => item.palette.primary === normalizedThemeColor)
  if (preset?.id === 'sage') return SAGE_BUSINESS_BUTTON_BASE

  const palette = preset?.palette || deriveThemePalette(normalizedThemeColor)
  const values = {
    secondary: [
      mixHex(SAGE_BUSINESS_BUTTON_BASE.secondary[0], palette.primaryLight, 0.35),
      mixHex(SAGE_BUSINESS_BUTTON_BASE.secondary[1], palette.primaryDark, 0.25),
      mixHex(SAGE_BUSINESS_BUTTON_BASE.secondary[2], palette.headerBorder, 0.35),
    ],
    disabled: [
      mixHex(SAGE_BUSINESS_BUTTON_BASE.disabled[0], palette.primaryLight, 0.2),
      SAGE_BUSINESS_BUTTON_BASE.disabled[1],
      mixHex(SAGE_BUSINESS_BUTTON_BASE.disabled[2], palette.headerBorder, 0.25),
    ],
  }

  for (const role of ['success', 'warning', 'danger', 'info']) {
    values[role] = [
      mixHex(SAGE_BUSINESS_BUTTON_BASE[role][0], palette.primary, 0.06),
      mixHex(SAGE_BUSINESS_BUTTON_BASE[role][1], palette.primary, 0.1),
      mixHex(SAGE_BUSINESS_BUTTON_BASE[role][2], palette.primary, 0.08),
    ]
  }

  return values
}

/**
 * 根据主题色与可选独立按钮色生成业务内容区按钮语义色板。
 *
 * @param {string} themeColor 当前主题色
 * @param {string} buttonColor 当前主按钮色
 * @returns {object} 业务按钮语义色板
 */
export function resolveBusinessButtonPalette(themeColor, buttonColor = themeColor) {
  const primaryBackground = resolveBusinessPrimaryBackground(buttonColor)
  const values = resolveThemeBusinessButtonValues(themeColor)
  const normalizedButtonColor = normalizeHex(buttonColor)
  const roles = {
    primary: createBusinessButtonRole(
      [primaryBackground, WHITE, primaryBackground],
      { primary: true },
    ),
    secondary: createBusinessButtonRole(values.secondary),
    success: createBusinessButtonRole(values.success),
    warning: createBusinessButtonRole(values.warning),
    danger: createBusinessButtonRole(values.danger),
    info: createBusinessButtonRole(values.info),
    disabled: createBusinessButtonRole(values.disabled, { disabled: true }),
  }

  return Object.freeze({
    ...roles,
    focusRing: normalizedButtonColor === THEME_PRESETS[0].palette.primary
      ? '#AAB8AC'
      : mixHex(primaryBackground, WHITE, 0.42),
  })
}
